import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { useNavigate } from "@tanstack/react-router";
import { DriverIcon, LiveDriverMap, type DriverPin } from "@the-drop/live-driver-map";
import type { RunEtaPayload } from "@the-drop/types";
import { isLikelyGoogleMapsJavaScriptApiKey } from "../../lib/google-maps-key";
import type { MapDriverPin } from "./delivery-map-types";
import { RunEtaInline } from "./RunEtaInline";

export type { MapDriverPin } from "./delivery-map-types";

type Props = {
  drivers: MapDriverPin[];
  /**
   * Live driver positions keyed by `driverId`, populated by `useTransporterDriversLive` from the
   * shared transporter socket. Drivers in `drivers` without a position entry render with no pin yet.
   */
  positions: Record<string, { lat: number; lng: number }>;
  /**
   * Live `run.eta` payload for the selected driver's active run, if any. Parent resolves driverId
   * → runId from the active-runs API and looks the entry up in the `useTransporterRunEta` map.
   * Undefined when no driver is selected, the selected driver has no active run, or no `run.eta`
   * has arrived yet for their run.
   */
  selectedRunEta?: RunEtaPayload;
  selectedDriverId: string | null;
  onSelectDriverId: (id: string | null) => void;
};

/** Default map center (Denver) — same fallback the customer-web tracking map uses. */
const MAP_DEFAULT_CENTER = { lat: 39.7392, lng: -104.9903 };

export function DeliveryMapPanel({
  drivers,
  positions,
  selectedRunEta,
  selectedDriverId,
  onSelectDriverId,
}: Props) {
  const navigate = useNavigate();
  const selected = drivers.find((d) => d.driverId === selectedDriverId) ?? null;

  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  const mapsKeyLooksValid = isLikelyGoogleMapsJavaScriptApiKey(mapsApiKey);

  // DriverPin set fed to the map: every API-fetched driver, with `position` populated from the live
  // WS positions map (null when we haven't received a GPS fix for that driverId yet).
  const driverPins: DriverPin[] = drivers.map((d) => ({
    driverId: d.driverId,
    position: positions[d.driverId] ?? null,
  }));

  return (
    <div className="td-surface-soft relative z-0 flex w-full shrink-0 flex-col overflow-hidden lg:w-[300px]">
      <div className="relative z-10 flex items-center justify-between border-b border-[#F0F0F0] bg-white px-5 py-4">
        <div>
          <span className="text-sm font-bold text-td-ink">Delivery Map</span>
          {selected ? (
            <span className="ml-2 text-xs text-td-muted">— {selected.name.split(" ")[0]}</span>
          ) : null}
        </div>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-1 text-xs font-medium text-[#555555] hover:text-td-ink"
          onClick={() => void navigate({ to: "/workspace/runs" })}
        >
          View All
          <ChevronRightIcon />
        </button>
      </div>

      {mapsApiKey && !mapsKeyLooksValid ? (
        <div className="relative z-10 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-td-ink">
          <span className="font-mono">VITE_GOOGLE_MAPS_API_KEY</span> is set but does not look like a Google Maps
          JavaScript API key (expected to start with <span className="font-mono">AIza</span>). Showing schematic map.
        </div>
      ) : null}

      <div className="relative isolate z-0 h-[220px] overflow-hidden bg-[#E8E8E4]">
        {mapsKeyLooksValid ? (
          <LiveDriverMap
            apiKey={mapsApiKey}
            mapId="transporter-fleet"
            drivers={driverPins}
            fallbackCenter={MAP_DEFAULT_CENTER}
            renderDriverMarker={({ driverId, position }) => {
              const driverMeta = drivers.find((d) => d.driverId === driverId) ?? null;
              const isSelected = driverId === selectedDriverId;
              const isDimmed = selectedDriverId != null && !isSelected;
              return (
                <AdvancedMarker
                  position={position}
                  title={driverMeta?.name}
                  zIndex={isSelected ? 1000 : 1}
                  onClick={() => onSelectDriverId(isSelected ? null : driverId)}
                >
                  <div
                    className="transition-transform hover:scale-110"
                    style={{
                      opacity: isDimmed ? 0.35 : 1,
                      transform: isSelected ? "scale(1.15)" : "scale(0.9)",
                      transformOrigin: "center",
                      cursor: "pointer",
                    }}
                  >
                    <DriverIcon color={isSelected ? "#111111" : "#1f6e4e"} />
                  </div>
                </AdvancedMarker>
              );
            }}
          />
        ) : (
          <DeliveryMapSchematic
            drivers={drivers}
            positions={positions}
            selectedDriverId={selectedDriverId}
            onSelectDriverId={onSelectDriverId}
          />
        )}
      </div>

      {!mapsKeyLooksValid && !mapsApiKey ? (
        <div className="relative z-10 border-b border-[#F0F0F0] bg-white px-3 py-2 text-[11px] text-td-muted">
          Set <span className="font-mono">VITE_GOOGLE_MAPS_API_KEY</span> to show Google Maps (same key as customer order
          tracking).
        </div>
      ) : null}

      {selected ? (
        <div className="relative z-10 flex items-center gap-3 border-t border-[#F0F0F0] bg-white px-5 py-3.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-td-green">
            <LocationIcon className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-td-ink">
              {selected.hasActiveRun ? "Active run" : "No active run"}
            </div>
            {selected.hasActiveRun && selectedRunEta ? (
              <RunEtaInline runEta={selectedRunEta} />
            ) : (
              <div className="text-xs text-td-muted">
                {selected.hasActiveRun ? "Live position shown on map" : "Assign or start a run to see live tracking"}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative z-10 border-t border-[#F0F0F0] bg-white px-5 py-3.5">
          <span className="text-xs text-td-muted-light">Select a driver to highlight on the map</span>
        </div>
      )}
    </div>
  );
}

/**
 * SVG fallback used when no valid Google Maps key is configured. Plots real live positions only
 * (no synthetic hashing) — drivers without a `driver.location` event yet aren't shown.
 */
function DeliveryMapSchematic({
  drivers,
  positions,
  selectedDriverId,
  onSelectDriverId,
}: Props) {
  const positioned = drivers
    .map((d) => {
      const pos = positions[d.driverId];
      if (!pos) return null;
      return { driverId: d.driverId, name: d.name, lat: pos.lat, lng: pos.lng };
    })
    .filter((d): d is { driverId: string; name: string; lat: number; lng: number } => d != null);

  const bounds =
    positioned.length > 0
      ? {
          minX: Math.min(...positioned.map((p) => p.lng)),
          maxX: Math.max(...positioned.map((p) => p.lng)),
          minY: Math.min(...positioned.map((p) => p.lat)),
          maxY: Math.max(...positioned.map((p) => p.lat)),
        }
      : { minX: -105.1, maxX: -104.9, minY: 39.6, maxY: 39.8 };

  const pad = 0.02;
  const w = bounds.maxX - bounds.minX || 0.1;
  const h = bounds.maxY - bounds.minY || 0.1;

  const project = (lat: number, lng: number) => {
    const x = ((lng - bounds.minX + pad) / (w + pad * 2)) * 100;
    const y = 100 - ((lat - bounds.minY + pad) / (h + pad * 2)) * 100;
    return { x: Math.min(95, Math.max(5, x)), y: Math.min(95, Math.max(5, y)) };
  };

  return (
    <>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#D4D4D0" strokeWidth="0.15" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#grid)" />
      </svg>

      {positioned.map((p) => {
        const { x, y } = project(p.lat, p.lng);
        const isSelected = p.driverId === selectedDriverId;
        const isDimmed = selectedDriverId != null && !isSelected;
        const size = isSelected ? 11 : 8;
        return (
          <button
            key={p.driverId}
            type="button"
            title={p.name}
            className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md transition-transform hover:scale-105 ${
              isDimmed ? "opacity-35" : "opacity-100"
            } ${isSelected ? "bg-[#111111]" : "bg-td-green"}`}
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: size,
              height: size,
            }}
            onClick={() => onSelectDriverId(isSelected ? null : p.driverId)}
          />
        );
      })}
    </>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
